import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { getIsZendeskInitedSelector } from '../zendeskSelectors';
import appHistory, { appHistoryPushWithSearchParams, excludeSearchParam } from '../../../app/appHistory';
import useZendeskWebWidgetInstance from "./useZendeskWebWidgetInstance";
import { IConversationField } from "../Zendesk";

function useZendeskHandleOpen() {
    const isWebWidgetInited = useSelector(getIsZendeskInitedSelector);
    const location = useLocation();
    const widgetOpenPrev = useRef<boolean | null>(null);
    const zendeskWebWidgetInstance = useZendeskWebWidgetInstance();
    const isWidgetOpen = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return !!searchParams.get('support-widget');
    }, [location.search]);

    const openWebWidget = useCallback((conversationFields?: IConversationField[]) => {
        zendeskWebWidgetInstance.setConversationFields(conversationFields);
        appHistoryPushWithSearchParams({ 'support-widget': '1' });
    }, [zendeskWebWidgetInstance]);

    const closeWebWidget = useCallback(() => {
        const searchParams = excludeSearchParam('support-widget', '1');
        appHistory.replace({
            search: searchParams.toString(),
        });
    }, []);

    useEffect(() => {
        if (!isWebWidgetInited) {
            return;
        }

        if (widgetOpenPrev.current === isWidgetOpen) {
            return;
        }

        if (isWidgetOpen) {
            zendeskWebWidgetInstance.show();
            zendeskWebWidgetInstance.open();
        } else {
            zendeskWebWidgetInstance.close();
        }
        widgetOpenPrev.current = isWidgetOpen;
    }, [isWebWidgetInited, location, zendeskWebWidgetInstance, isWidgetOpen]);

    return {
        openWebWidget,
        closeWebWidget,
        isWidgetOpen,
    }
}

export default useZendeskHandleOpen;
